import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Home,
  FileText,
  Settings,
  BarChart3,
  Calculator,
  Sun,
  Moon,
  Link2,
  Bug,
  Zap,
  Shield,
  GitBranch,
} from "lucide-react";

type CommandAction = {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  action: () => void;
  keywords?: string;
};

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const go = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  const toggleTheme = () => {
    const root = document.documentElement;
    root.classList.toggle("dark");
    setOpen(false);
  };

  const pages: CommandAction[] = [
    { label: "Home", icon: Home, action: () => go("/"), keywords: "landing index" },
    { label: "Changelog", icon: FileText, action: () => go("/changelog"), keywords: "updates releases" },
    { label: "Cost Calculator", icon: Calculator, action: () => go("/cost-calculator"), keywords: "pricing savings" },
    { label: "Dashboard", icon: BarChart3, action: () => go("/dashboard") },
    { label: "Errors", icon: Bug, action: () => go("/errors"), keywords: "bugs issues" },
    { label: "Analytics", icon: BarChart3, action: () => go("/analytics") },
    { label: "Connections", icon: Link2, action: () => go("/connections") },
    { label: "Settings", icon: Settings, action: () => go("/settings") },
  ];

  const integrations: CommandAction[] = [
    { label: "Sentry", icon: Bug, action: () => go("/integrations/sentry"), keywords: "error monitoring" },
    { label: "Datadog", icon: BarChart3, action: () => go("/integrations/datadog"), keywords: "monitoring apm" },
    { label: "New Relic", icon: Zap, action: () => go("/integrations/newrelic"), keywords: "observability" },
    { label: "Zipy", icon: Shield, action: () => go("/integrations/zipy"), keywords: "session replay" },
  ];

  const platforms: CommandAction[] = [
    { label: "GitHub", icon: GitBranch, action: () => go("/platforms/github"), keywords: "git repo" },
    { label: "GitLab", icon: GitBranch, action: () => go("/platforms/gitlab"), keywords: "git repo" },
    { label: "Bitbucket", icon: GitBranch, action: () => go("/platforms/bitbucket"), keywords: "git repo" },
    { label: "Lovable", icon: Zap, action: () => go("/platforms/lovable"), keywords: "lovable zipy healion auto-fix" },
  ];

  const actions: CommandAction[] = [
    { label: "Toggle Light Mode", icon: Sun, action: toggleTheme, keywords: "theme light" },
    { label: "Toggle Dark Mode", icon: Moon, action: toggleTheme, keywords: "theme dark" },
  ];

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Pages">
          {pages.map((item) => (
            <CommandItem key={item.label} onSelect={item.action} keywords={item.keywords ? [item.keywords] : undefined}>
              <item.icon className="mr-2 h-4 w-4 text-muted-foreground" />
              {item.label}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Integrations">
          {integrations.map((item) => (
            <CommandItem key={item.label} onSelect={item.action} keywords={item.keywords ? [item.keywords] : undefined}>
              <item.icon className="mr-2 h-4 w-4 text-muted-foreground" />
              {item.label}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Platforms">
          {platforms.map((item) => (
            <CommandItem key={item.label} onSelect={item.action} keywords={item.keywords ? [item.keywords] : undefined}>
              <item.icon className="mr-2 h-4 w-4 text-muted-foreground" />
              {item.label}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Actions">
          {actions.map((item) => (
            <CommandItem key={item.label} onSelect={item.action} keywords={item.keywords ? [item.keywords] : undefined}>
              <item.icon className="mr-2 h-4 w-4 text-muted-foreground" />
              {item.label}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
